import { Controller } from "@hotwired/stimulus"

/**
 * Flash Messages Controller
 *
 * Provides animated, dismissible flash messages with:
 * - Slide-in animations
 * - Auto-dismiss with progress bar
 * - Manual dismiss with button
 * - Multiple message types (success, error, warning, info)
 * - Stacking support for multiple messages
 *
 * Usage:
 *   <div data-controller="flash" data-flash-type-value="success" data-flash-duration-value="5000">
 *     <p data-flash-target="message">Your message here</p>
 *   </div>
 *
 * Or programmatically:
 *   window.showFlash('Message', 'success', 5000)
 */
export default class extends Controller {
  static targets = ["message", "progress", "container"]
  static values = {
    type: { type: String, default: "info" },
    duration: { type: Number, default: 5000 },
    dismissible: { type: Boolean, default: true },
    autoShow: { type: Boolean, default: true }
  }

  connect() {
    // Register global function
    if (!window.showFlash) {
      window.showFlash = this.showGlobal.bind(this)
    }

    // Auto-show the flash message with animation
    if (this.autoShowValue) {
      this.show()
    }
  }

  disconnect() {
    this.clearTimers()
  }

  show() {
    // Trigger slide-in animation
    requestAnimationFrame(() => {
      this.element.classList.remove('translate-y-2', 'opacity-0', 'scale-95')
      this.element.classList.add('translate-y-0', 'opacity-100', 'scale-100')
    })

    // Start auto-dismiss timer if duration > 0
    if (this.durationValue > 0) {
      this.startProgressBar()
      this.dismissTimer = setTimeout(() => {
        this.dismiss()
      }, this.durationValue)
    }
  }

  dismiss() {
    this.clearTimers()

    // Trigger slide-out animation
    this.element.classList.add('translate-y-2', 'opacity-0', 'scale-95')
    this.element.classList.remove('translate-y-0', 'opacity-100', 'scale-100')

    // Remove element after animation
    setTimeout(() => {
      this.element.remove()
    }, 300)
  }

  pause() {
    // Pause auto-dismiss on hover
    this.clearTimers()
    if (this.hasProgressTarget) {
      this.progressTarget.style.animationPlayState = 'paused'
    }
  }

  resume() {
    // Resume auto-dismiss when mouse leaves
    if (this.durationValue > 0 && !this.dismissed) {
      const remaining = this.getRemainingTime()
      if (remaining > 0) {
        this.dismissTimer = setTimeout(() => {
          this.dismiss()
        }, remaining)

        if (this.hasProgressTarget) {
          this.progressTarget.style.animationPlayState = 'running'
        }
      }
    }
  }

  startProgressBar() {
    if (this.hasProgressTarget) {
      this.progressTarget.style.animationDuration = `${this.durationValue}ms`
      this.progressTarget.classList.add('animate-progress')
      this.startTime = Date.now()
    }
  }

  getRemainingTime() {
    if (!this.startTime) return this.durationValue
    const elapsed = Date.now() - this.startTime
    return Math.max(0, this.durationValue - elapsed)
  }

  clearTimers() {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer)
      this.dismissTimer = null
    }
  }

  // Global method to show flash messages programmatically
  showGlobal(message, type = 'info', duration = 5000) {
    const container = document.getElementById('flash-container') || this.createContainer()
    const flash = this.createFlashElement(message, type, duration)
    container.appendChild(flash)

    // Initialize the controller on the new element
    const controller = this.application.getControllerForElementAndIdentifier(flash, 'flash')
    if (controller) {
      controller.show()
    }

    return flash
  }

  createContainer() {
    const container = document.createElement('div')
    container.id = 'flash-container'
    container.className = 'fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-md w-full pointer-events-none'
    container.setAttribute('aria-live', 'polite')
    container.setAttribute('aria-atomic', 'true')
    document.body.appendChild(container)
    return container
  }

  createFlashElement(message, type, duration) {
    const colors = {
      success: {
        bg: 'bg-green-50',
        border: 'border-green-500',
        text: 'text-green-800',
        icon: 'text-green-500',
        progress: 'bg-green-500'
      },
      error: {
        bg: 'bg-red-50',
        border: 'border-red-500',
        text: 'text-red-800',
        icon: 'text-red-500',
        progress: 'bg-red-500'
      },
      warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-500',
        text: 'text-yellow-800',
        icon: 'text-yellow-500',
        progress: 'bg-yellow-500'
      },
      info: {
        bg: 'bg-blue-50',
        border: 'border-blue-500',
        text: 'text-blue-800',
        icon: 'text-blue-500',
        progress: 'bg-blue-500'
      },
      notice: {
        bg: 'bg-green-50',
        border: 'border-green-500',
        text: 'text-green-800',
        icon: 'text-green-500',
        progress: 'bg-green-500'
      },
      alert: {
        bg: 'bg-red-50',
        border: 'border-red-500',
        text: 'text-red-800',
        icon: 'text-red-500',
        progress: 'bg-red-500'
      }
    }

    const icons = {
      success: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>`,
      error: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>`,
      warning: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>`,
      info: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>`,
      notice: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>`,
      alert: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>`
    }

    const color = colors[type] || colors.info
    const icon = icons[type] || icons.info

    const flash = document.createElement('div')
    flash.className = `
      pointer-events-auto
      transform translate-y-2 opacity-0 scale-95
      transition-all duration-300 ease-out
      ${color.bg} ${color.border} border-l-4
      rounded-lg shadow-lg overflow-hidden
    `.trim().replace(/\s+/g, ' ')

    flash.setAttribute('data-controller', 'flash')
    flash.setAttribute('data-flash-type-value', type)
    flash.setAttribute('data-flash-duration-value', duration)
    flash.setAttribute('data-flash-auto-show-value', 'true')
    flash.setAttribute('data-action', 'mouseenter->flash#pause mouseleave->flash#resume')
    flash.setAttribute('role', 'alert')

    flash.innerHTML = `
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 ${color.icon}" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              ${icon}
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <p class="${color.text} text-sm font-medium" data-flash-target="message">
              ${this.escapeHtml(message)}
            </p>
          </div>
          <div class="ml-4 flex-shrink-0">
            <button type="button"
                    class="${color.text} hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-md"
                    data-action="click->flash#dismiss"
                    aria-label="Dismiss">
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      ${duration > 0 ? `
        <div class="h-1 w-full bg-gray-200 bg-opacity-50">
          <div class="${color.progress} h-full origin-left"
               data-flash-target="progress"
               style="animation: shrink ${duration}ms linear forwards;"></div>
        </div>
      ` : ''}
    `

    return flash
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
